import { useMemo, useState } from 'react';

import { isTaskDueSoon, isTaskExpired } from '@/components/TaskExpiringLabel/taskExpiration';
import { useDueTasks } from '@/hooks/useDueTasks';
import type { TaskResponse, TaskStatusType } from '@/store/tasks/tasks.dto';
import { TaskStatus } from '@/store/tasks/tasks.dto';
import { useTasksListQuery } from '@/store/tasks/tasks.queries';

/**
 * The Maintain queue's data. Nothing is read that the section does not print.
 *
 * - "Needs doing" is a `limit=1` request: `count` is computed over the whole filter
 *   rather than over the page, so one row buys an exact total.
 * - "Overdue" and "Due soon" come from `useDueTasks` — the jobs that could carry a due
 *   marker at all, which is the same set the equipment grid needs for its category
 *   badges. Both sections share the query key, so the page fetches them once.
 * - the list asks for `QUEUE_ROWS` and renders two of them when the overdue banner takes
 *   a slot, so a vessel with four hundred outstanding jobs fetches the same three rows
 *   as one with four.
 */

/** Everything still outstanding. Wider than the two statuses the due predicates read. */
const OPEN_STATUSES: TaskStatusType[] = [
  TaskStatus.PENDING,
  TaskStatus.IN_PROGRESS,
  TaskStatus.ON_HOLD,
  TaskStatus.ON_APPROVAL,
];

/** `equipment IS NOT NULL` — the maintenance cut, so plain tasks never travel. */
const MAINTENANCE = { equipment_id: 'all' } as const;

/**
 * Rows the queue asks for. Three is what it renders at most — two when the overdue
 * banner takes the first slot — so this is the display's own number, not a scan budget.
 *
 * Caveat worth knowing: `GET /tasks` sorts by `time_slot.start` and takes no order
 * parameter, so these are the three that *start* soonest among the jobs still ahead,
 * which is not always the three due soonest. They are re-sorted by deadline for display,
 * but a job that starts later and is due earlier can be missed. An `order` parameter on
 * the service is what closes that gap.
 */
export const QUEUE_ROWS = 3;

/**
 * Far enough out to be no bound at all. The endpoint reads a `from`/`to` *pair* as
 * "ends after `from`, starts before `to`" (`internal/pkg/database/task.go:685`), which is
 * the only way to ask it for "deadline not yet passed" — `from` on its own switches to
 * `start >= from`, a different question. The upper bound is inert.
 */
const NO_UPPER_BOUND = '9999-12-31T23:59:59Z';

/** Due soon first, then by deadline — the order the queue has always listed in. */
const byUrgency = (a: TaskResponse, b: TaskResponse) => {
  const rank = (task: TaskResponse) => (isTaskDueSoon(task) ? 0 : 1);
  return rank(a) - rank(b) || a.time_slot.end.localeCompare(b.time_slot.end);
};

export type MaintenanceQueueData = {
  overdueCount: number;
  dueSoonCount: number;
  needsDoingCount: number;
  /** Still to come, most urgent first. Overdue jobs are not among them. */
  upcoming: TaskResponse[];
  /** Any of the three below. */
  isPending: boolean;
  /** Per request, so each tile holds its bone only until its own figure lands. */
  isDuePending: boolean;
  isNeedsDoingPending: boolean;
  isUpcomingPending: boolean;
};

export const useMaintenanceDueCounts = () => {
  const { tasks: dueTasks, isPending } = useDueTasks();

  return useMemo(
    () => ({
      overdueCount: dueTasks.filter(isTaskExpired).length,
      dueSoonCount: dueTasks.filter(isTaskDueSoon).length,
      isPending,
    }),
    [dueTasks, isPending],
  );
};

export const useMaintenanceQueueData = (): MaintenanceQueueData => {
  // Frozen at mount: it rides in a query key, so a ticking clock would mean a fresh
  // cache entry every render. The queue is a snapshot of the moment you arrived, and any
  // write to a task invalidates it.
  const [now] = useState(() => new Date().toISOString());

  // "Needs doing" — one row, exact total.
  const needsDoingQuery = useTasksListQuery({ ...MAINTENANCE, limit: 1, status: OPEN_STATUSES }, 1);

  // Both due figures, off the set the equipment grid is reading anyway. Every overdue
  // job is in it by definition — its deadline is in the past, which is inside any
  // forward horizon — so this is the exact count, not a sample of one.
  const { overdueCount, dueSoonCount, isPending: isDuePending } = useMaintenanceDueCounts();

  // The rows the list renders, and nothing more.
  const rowsQuery = useTasksListQuery(
    { ...MAINTENANCE, limit: QUEUE_ROWS, status: OPEN_STATUSES, from: now, to: NO_UPPER_BOUND },
    1,
  );

  const rows = rowsQuery.data?.items;
  const upcoming = useMemo(
    // The service already excluded the overdue ones by deadline; this guard keeps the
    // two agreeing if a job's deadline passes while the page is open.
    () => (rows ?? []).filter((task) => !isTaskExpired(task)).sort(byUrgency),
    [rows],
  );

  return {
    overdueCount,
    dueSoonCount,
    needsDoingCount: needsDoingQuery.data?.count ?? 0,
    upcoming,
    isPending: needsDoingQuery.isPending || isDuePending || rowsQuery.isPending,
    isDuePending,
    isNeedsDoingPending: needsDoingQuery.isPending,
    isUpcomingPending: rowsQuery.isPending,
  };
};
